import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Search, ShieldCheck, Gauge } from "lucide-react";
import { HERO_IMG, BRANDS, FUELS, PRICE_STEPS } from "@/lib/site";

const LINES = [
  { text: "L'AUTO CHE", accent: false },
  { text: "CERCHI È", accent: false },
  { text: "GIÀ QUI.", accent: true },
];

export const Hero = ({ onSearch, onExplore }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);

  const [brand, setBrand] = useState("all");
  const [fuel, setFuel] = useState("all");
  const [price, setPrice] = useState("all");

  const submit = (e) => {
    e.preventDefault();
    onSearch({
      brand: brand === "all" ? "" : brand,
      fuel: fuel === "all" ? "" : fuel,
      maxPrice: price === "all" ? "" : price,
    });
  };

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Auto in primo piano - Manno Auto"
          className="w-full h-full object-cover object-[center_35%]"
        />
        <div className="absolute inset-0 hero-vignette" />
      </motion.div>

      <div className="relative z-10 flex-1 flex items-center max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-12 pt-32 pb-10">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.7 }}
            className="font-mono2 text-xs uppercase tracking-[0.35em] text-amber-500 mb-6"
            data-testid="hero-eyebrow"
          >
            Concessionaria Manno Auto S.r.l. — Cattolica Eraclea (AG)
          </motion.p>

          <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight text-[15vw] sm:text-[11vw] lg:text-[7.5rem]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "text-amber-500" : "text-gray-100"}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.4 + i * 0.16, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-gray-400 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Usato garantito, Km 0 e nuove plurimarca. Finanziamenti su misura e
            sub-agenzia assicurativa: da Manno Auto la tua prossima auto è un affare certo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-btn"
              onClick={() => onExplore("#catalogo")}
              className="bg-amber-500 hover:bg-amber-400 text-[#0A0D14] font-semibold px-8 py-4 rounded-full transition-all hover:shadow-[0_0_40px_rgba(245,158,11,0.4)] hover:-translate-y-0.5"
            >
              Esplora il Parco Auto
            </button>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <span className="flex items-center gap-2" data-testid="hero-badge-stock">
                <Gauge className="w-4 h-4 text-amber-500" /> Oltre 50 veicoli in pronta consegna
              </span>
              <span className="hidden sm:flex items-center gap-2" data-testid="hero-badge-warranty">
                <ShieldCheck className="w-4 h-4 text-amber-500" /> Garanzia 12–24 mesi
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-12 pb-14"
        data-testid="hero-search-form"
      >
        <div className="glass border border-border rounded-2xl p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-4 gap-3 shadow-2xl">
          <select
            data-testid="hero-filter-brand"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="bg-[#161D2B] border border-border rounded-xl px-4 py-3.5 text-sm text-gray-200 focus:outline-none focus:border-amber-500/60"
          >
            <option value="all">Tutte le marche</option>
            {BRANDS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <select
            data-testid="hero-filter-fuel"
            value={fuel}
            onChange={(e) => setFuel(e.target.value)}
            className="bg-[#161D2B] border border-border rounded-xl px-4 py-3.5 text-sm text-gray-200 focus:outline-none focus:border-amber-500/60"
          >
            <option value="all">Tutte le alimentazioni</option>
            {FUELS.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <select
            data-testid="hero-filter-price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="bg-[#161D2B] border border-border rounded-xl px-4 py-3.5 text-sm text-gray-200 focus:outline-none focus:border-amber-500/60"
          >
            <option value="all">Qualsiasi prezzo</option>
            {PRICE_STEPS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <button
            data-testid="hero-search-submit"
            type="submit"
            className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0A0D14] font-semibold rounded-xl px-6 py-3.5 transition-colors"
          >
            <Search className="w-4 h-4" />
            Cerca
          </button>
        </div>
      </motion.form>
    </section>
  );
};
